import type {
  DocumentCitation,
  FindingCitation,
  IssueProposal,
  IssueProposalAssignee,
} from "../types";

type JsonObject = Record<string, unknown>;
const text = (value: unknown): string => (typeof value === "string" ? value : "");
const nonemptyText = (value: unknown): string | null => text(value) || null;
const isId = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;
const citationPage = (value: unknown): number | null => (isId(value) ? value : null);

function documentCitation(c: JsonObject): DocumentCitation | null {
  if (typeof c.document_id !== "number") return null;
  const role = c.role === "brief" ? "brief" : "document";
  const document_id = c.document_id;
  const page = citationPage(c.page);
  const quote = text(c.quote);
  if (typeof c.observation === "string")
    return { role, evidence: "visual", document_id, page, observation: c.observation };
  if (typeof c.element_guid === "string" && c.element_guid) {
    return {
      role,
      evidence: "ifc",
      document_id,
      element_guid: c.element_guid,
      element_name: nonemptyText(c.element_name),
      quote,
    };
  }
  if (typeof c.sheet === "string" || typeof c.cells === "string") {
    return {
      role,
      evidence: "cells",
      document_id,
      sheet: text(c.sheet),
      cells: text(c.cells),
      page,
      quote,
    };
  }
  return { role, document_id, page, quote };
}

function buildingCitation(c: JsonObject): FindingCitation | null {
  const attribute_key = nonemptyText(c.attribute_key);
  return attribute_key
    ? { role: "building_data", attribute_key, label: nonemptyText(c.label), value: text(c.value) }
    : null;
}

function citation(entry: unknown): FindingCitation | null {
  if (typeof entry !== "object" || entry === null) return null;
  const c = entry as JsonObject;
  if (c.role === "issue" || typeof c.issue_id === "number") {
    return typeof c.issue_id === "number"
      ? { role: "issue", issue_id: c.issue_id, quote: text(c.quote) }
      : null;
  }
  if (c.role === "building_data") return buildingCitation(c);
  return documentCitation(c);
}

/** Read agent-written JSON defensively without dropping other valid citations. */
export function citationsOf(json: unknown): FindingCitation[] {
  if (!Array.isArray(json)) return [];
  return json.flatMap((entry) => {
    const parsed = citation(entry);
    return parsed ? [parsed] : [];
  });
}

function proposalAssignees(value: unknown): IssueProposalAssignee[] {
  if (!Array.isArray(value)) return [];
  const assignees: IssueProposalAssignee[] = [];
  for (const entry of value) {
    if (typeof entry !== "object" || entry === null) continue;
    const a = entry as JsonObject;
    if (!isId(a.user_profile_id)) continue;
    if (assignees.some((x) => x.user_profile_id === a.user_profile_id)) continue;
    assignees.push({ user_profile_id: a.user_profile_id, reason: text(a.reason).trim() });
  }
  return assignees;
}

/** A missing title rejects a proposal; malformed assignees are dropped individually. */
export function issueProposalOf(json: unknown): IssueProposal | null {
  if (typeof json !== "object" || json === null || Array.isArray(json)) return null;
  const p = json as JsonObject;
  const title = text(p.title).trim();
  if (!title) return null;
  return {
    title,
    rationale: text(p.rationale).trim(),
    discipline_ids: Array.isArray(p.discipline_ids)
      ? [...new Set(p.discipline_ids.filter(isId))]
      : [],
    assignees: proposalAssignees(p.assignees),
  };
}
